import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GlobalIncident } from '../../types/ScenarioManagement';
import { useTheme } from '../../contexts/ThemeContext';
import { JSONPersistenceService } from '../../services/JSONPersistenceService';
import { toast } from 'react-hot-toast';
import { IncidentForm } from './IncidentForm';
import { StandardButton } from '../ui';

type ViewMode = 'list' | 'create' | 'edit';

const GlobalIncidentManager: React.FC = () => {
  const { isDark } = useTheme();
  const [globalIncidents, setGlobalIncidents] = useState<GlobalIncident[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentMode, setCurrentMode] = useState<ViewMode>('list');
  const [selectedIncident, setSelectedIncident] = useState<GlobalIncident | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');
  const [expandedIncident, setExpandedIncident] = useState<string | null>(null);

  useEffect(() => {
    loadGlobalIncidents();
  }, []);

  const loadGlobalIncidents = async () => {
    try {
      setLoading(true);
      const persistence = JSONPersistenceService.getInstance();
      const data = await persistence.loadData();
      
      if (data?.globalIncidents) {
        const incidents = data.globalIncidents.map(([id, incident]) => incident);
        setGlobalIncidents(incidents);
      } else {
        // If globalIncidents is missing, initialize from data file
        const { incidents } = await import('../../data/incidents');
        const globalIncidentsData: GlobalIncident[] = incidents.map(inc => ({
          id: inc.id,
          title: inc.title,
          icon: '🚨',
          description: inc.description,
          severity: inc.severity,
          category: inc.category as GlobalIncident['category'],
          timeLimit: inc.timeLimit,
          potentialImpact: inc.potentialImpact,
          responseOptions: inc.responseOptions,
          isGlobal: true,
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: 'System',
          isEnabled: true,
          tags: ['system', 'default']
        }));
        
        setGlobalIncidents(globalIncidentsData);
        
        // Save to persistence
        const globalIncidentsMap = new Map(globalIncidentsData.map(incident => [incident.id, incident]));
        await persistence.saveData({
          scenarios: data?.scenarios || [],
          templates: data?.templates || [],
          categories: data?.categories || [],
          tags: data?.tags || [],
          dependencies: data?.dependencies || [],
          globalInvestments: data?.globalInvestments || [],
          globalIncidents: Array.from(globalIncidentsMap.entries())
        });
      }
    } catch (error) {
      console.error('Failed to load incidents from library:', error);
      toast.error('Failed to load incidents from library');
    } finally {
      setLoading(false);
    }
  };

  const saveGlobalIncidents = async (incidents: GlobalIncident[]) => {
    try {
      const persistence = JSONPersistenceService.getInstance();
      const data = await persistence.loadData();
      
      const globalIncidentsMap = new Map(incidents.map(incident => [incident.id, incident]));
      
      await persistence.saveData({
        scenarios: data?.scenarios || [],
        templates: data?.templates || [],
        categories: data?.categories || [],
        tags: data?.tags || [],
        dependencies: data?.dependencies || [],
        globalInvestments: data?.globalInvestments || [],
        globalIncidents: Array.from(globalIncidentsMap.entries())
      });
      
      setGlobalIncidents(incidents);
    } catch (error) {
      console.error('Failed to save incidents to library:', error);
      toast.error('Failed to save incidents to library');
    }
  };

  const handleCreateIncident = async (newIncident: Omit<GlobalIncident, 'id' | 'createdAt' | 'updatedAt'>) => {
    const incident: GlobalIncident = {
      ...newIncident,
      id: `incident_${Date.now()}`,
      createdAt: new Date(),
      updatedAt: new Date(),
      isGlobal: true
    };
    
    await saveGlobalIncidents([...globalIncidents, incident]);
    setCurrentMode('list');
    setSelectedIncident(null);
    toast.success('Incident created successfully');
  };

  const handleCreate = () => {
    setSelectedIncident(null);
    setCurrentMode('create');
  };

  const handleEdit = (incident: GlobalIncident) => {
    setSelectedIncident(incident);
    setCurrentMode('edit');
  };

  const handleCancel = () => {
    setCurrentMode('list');
    setSelectedIncident(null);
  };

  const handleUpdateIncident = async (updatedData: Omit<GlobalIncident, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!selectedIncident) return;
    
    const updatedIncident: GlobalIncident = {
      ...updatedData,
      id: selectedIncident.id,
      createdAt: selectedIncident.createdAt,
      updatedAt: new Date()
    };
    
    const updatedIncidents = globalIncidents.map(incident =>
      incident.id === updatedIncident.id ? updatedIncident : incident
    );
    
    await saveGlobalIncidents(updatedIncidents);
    setCurrentMode('list');
    setSelectedIncident(null);
    toast.success('Incident updated successfully');
  };

  const handleDeleteIncident = async (incidentId: string) => {
    const updatedIncidents = globalIncidents.filter(incident => incident.id !== incidentId);
    await saveGlobalIncidents(updatedIncidents);
    toast.success('Incident deleted successfully');
  };

  // TODO: Connect to UI when enable/disable functionality is needed
  // const handleToggleEnabled = async (incidentId: string) => {
  //   const updatedIncidents = globalIncidents.map(incident =>
  //     incident.id === incidentId 
  //       ? { ...incident, isEnabled: !incident.isEnabled, updatedAt: new Date() }
  //       : incident
  //   );
  //   
  //   await saveGlobalIncidents(updatedIncidents);
  //   toast.success('Incident status updated');
  // };

  const filteredIncidents = globalIncidents.filter(incident => {
    const matchesSearch = incident.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         incident.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || incident.category === selectedCategory;
    const matchesSeverity = selectedSeverity === 'all' || incident.severity === selectedSeverity;
    return matchesSearch && matchesCategory && matchesSeverity;
  });

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low': return 'text-green-500';
      case 'medium': return 'text-yellow-500';
      case 'high': return 'text-orange-500';
      case 'critical': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'low': return '🟢';
      case 'medium': return '🟡';
      case 'high': return '🟠';
      case 'critical': return '🔴';
      default: return '⚪';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'phishing': return '🎣';
      case 'malware': return '🦠';
      case 'ransomware': return '🔐';
      case 'supply-chain': return '🔗';
      case 'insider': return '👤';
      case 'ddos': return '⚡';
      case 'data-breach': return '💾';
      default: return '🚨';
    }
  };

  const toggleIncidentExpansion = (incidentId: string) => {
    setExpandedIncident(expandedIncident === incidentId ? null : incidentId);
  };

  const getImpactColor = (impact: number) => {
    if (impact > 0) return isDark ? 'text-green-400' : 'text-green-600';
    if (impact < 0) return isDark ? 'text-red-400' : 'text-red-600';
    return isDark ? 'text-slate-400' : 'text-slate-500';
  };

  const formatImpact = (impact: number) => {
    if (impact > 0) return `+${impact}`;
    return impact.toString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-professional-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-2xl font-light ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Incident Library
          </h2>
          <p className={`${isDark ? 'text-slate-300' : 'text-gray-600'} mt-1`}>
            Manage the library of cybersecurity incidents available across all scenarios
          </p>
        </div>
        {currentMode === 'list' && (
          <StandardButton
            onClick={handleCreate}
            variant="primary"
            leftIcon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            }
          >
            Add Incident
          </StandardButton>
        )}
      </div>

      {/* Create/Edit Form */}
      <AnimatePresence mode="wait">
        {currentMode !== 'list' && (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mb-6"
          >
            <IncidentForm
              incident={selectedIncident}
              onSave={currentMode === 'edit' ? handleUpdateIncident : handleCreateIncident}
              onCancel={handleCancel}
              mode={currentMode}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search and Filters */}
      {currentMode === 'list' && (
        <div className={`${isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200'} border rounded-lg p-4`}>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search incidents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full px-4 py-2 rounded-lg border ${
                isDark 
                  ? 'border-white/20 bg-white/5 text-white placeholder-slate-400' 
                  : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
              } focus:ring-2 focus:ring-professional-blue-500 focus:border-transparent`}
            />
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className={`px-4 py-2 rounded-lg border ${
              isDark 
                ? 'border-white/20 bg-white/5 text-white' 
                : 'border-gray-300 bg-white text-gray-900'
            } focus:ring-2 focus:ring-professional-blue-500 focus:border-transparent`}
          >
            <option value="all">All Categories</option>
            <option value="phishing">🎣 Phishing</option>
            <option value="malware">🦠 Malware</option>
            <option value="ransomware">🔐 Ransomware</option>
            <option value="supply-chain">🔗 Supply Chain</option>
            <option value="insider">👤 Insider Threat</option>
            <option value="ddos">⚡ DDoS</option>
            <option value="data-breach">💾 Data Breach</option>
          </select>
          <select
            value={selectedSeverity}
            onChange={(e) => setSelectedSeverity(e.target.value)}
            className={`px-4 py-2 rounded-lg border ${
              isDark 
                ? 'border-white/20 bg-white/5 text-white' 
                : 'border-gray-300 bg-white text-gray-900'
            } focus:ring-2 focus:ring-professional-blue-500 focus:border-transparent`}
          >
            <option value="all">All Severities</option>
            <option value="low">🟢 Low</option>
            <option value="medium">🟡 Medium</option>
            <option value="high">🟠 High</option>
            <option value="critical">🔴 Critical</option>
          </select>
        </div>
      </div>
      )}


      {/* Incidents List */}
      {currentMode === 'list' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        <AnimatePresence>
          {filteredIncidents.map((incident) => (
            <motion.div
              key={incident.id}
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className={`${
                isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200'
              } border rounded-lg p-4 hover:shadow-md transition-all duration-200 relative ${
                !incident.isEnabled ? 'opacity-60' : ''
              }`}
            >
              {/* Actions Menu */}
              <div className="absolute top-4 right-4 flex gap-1">
                <StandardButton
                  onClick={() => handleEdit(incident)}
                  variant="ghost"
                  size="small"
                  className="!p-1.5"
                  title="Edit incident"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                  </svg>
                </StandardButton>
                <StandardButton
                  onClick={() => handleDeleteIncident(incident.id)}
                  variant="ghost"
                  size="small"
                  className="!p-1.5 !text-red-400 hover:!text-red-300"
                  title="Delete incident"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                  </svg>
                </StandardButton>
              </div>

              <div className="mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{incident.icon}</span>
                  <div>
                    <span className="text-lg">{getCategoryIcon(incident.category)}</span>
                    <span className="text-lg ml-1">{getSeverityIcon(incident.severity)}</span>
                  </div>
                </div>
              </div>

              <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {incident.title}
              </h3>

              <p className={`text-sm mb-3 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                {incident.description}
              </p>

              <div className="flex items-center justify-between text-sm mb-3">
                <div className="flex items-center gap-4">
                  <span className={`${getSeverityColor(incident.severity)} font-medium`}>
                    {incident.severity.toUpperCase()}
                  </span>
                  <span className={`${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    {Math.floor(incident.timeLimit / 60)}m {incident.timeLimit % 60}s
                  </span>
                </div>
                <button
                  onClick={() => toggleIncidentExpansion(incident.id)}
                  className={`px-2 py-1 rounded text-xs transition-colors ${
                    isDark ? 'bg-white/10 text-slate-300 hover:bg-white/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {incident.responseOptions.length} options {expandedIncident === incident.id ? '▼' : '▶'}
                </button>
              </div>

              {/* Expandable Response Options */}
              <AnimatePresence>
                {expandedIncident === incident.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} pt-3 mt-3`}
                  >
                    <h4 className={`text-sm font-medium mb-2 ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>
                      Response Options:
                    </h4>
                    <div className="space-y-2">
                      {incident.responseOptions.map((option, optionIndex) => (
                        <div
                          key={option.id}
                          className={`p-2 rounded text-xs ${
                            isDark ? 'bg-white/[0.02] border border-white/10' : 'bg-slate-50 border border-slate-200'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-1">
                            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {option.label}
                            </span>
                            <span className={`${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                              {option.timeRequired}m
                            </span>
                          </div>
                          <p className={`${isDark ? 'text-slate-300' : 'text-gray-600'} mb-2`}>
                            {option.description}
                          </p>
                          <div className="flex gap-3 text-xs">
                            <span className={getImpactColor(option.impact.security)}>
                              S: {formatImpact(option.impact.security)}
                            </span>
                            <span className={getImpactColor(option.impact.reputation)}>
                              R: {formatImpact(option.impact.reputation)}
                            </span>
                            <span className={getImpactColor(option.impact.financial)}>
                              F: {formatImpact(option.impact.financial)}
                            </span>
                            <span className={getImpactColor(option.impact.operational)}>
                              O: {formatImpact(option.impact.operational)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
      )}

      {currentMode === 'list' && filteredIncidents.length === 0 && (
        <div className="text-center py-12">
          <p className={`text-lg ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
            No incidents found matching your criteria
          </p>
        </div>
      )}
    </div>
  );
};

export default GlobalIncidentManager;